'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import HtzLink from '@haaretz/s-htz-link';
import Icon from '@haaretz/s-icon';
import { useLoginApp } from '@haaretz/s-next-layout/LoginProvider';
import { usePurchasePagePersonalQuery } from '@haaretz/s-pp-queries/PurchasePage';
import getCurrentStep from '@haaretz/s-pp-routing-utils/getCurrentStep';
import getCurrentStepName from '@haaretz/s-pp-routing-utils/getCurrentStepName';
import isGqlPpStep from '@haaretz/s-pp-routing-utils/isGqlPpStep';
import useBi from '@haaretz/s-use-bi';
import usePpDataVariables from '@haaretz/s-use-pp-data-variables';
import VisuallyHidden from '@haaretz/s-visually-hidden';
import { useRouter } from 'next/router';
import * as React from 'react';
import s9 from 'style9';
const c = s9.create({
    base: {
        justifyItems: 'center',
        backgroundColor: "rgb(var(--c-footerBg))",
        color: "rgb(var(--c-footerColor))",
        display: 'grid',
        marginTop: 'auto',
        paddingBottom: "3rem",
        paddingTop: "2.5rem",
        textAlign: 'center',
        width: '100%',
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz-1)",
            lineHeight: "var(--lh-1)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz-0)",
            lineHeight: "var(--lh-0)"
        }
    },
    icon: {
        fontSize: "2rem",
        "@media all and (min-width:80em)": {
            fontSize: "2.25rem"
        },
        "@media all and (max-width:79.9375em)": {
            marginBottom: "0.5rem"
        }
    },
    homePageLink: {
        fontWeight: 700,
        marginTop: "0.5rem",
        fontFamily: '"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        "@media all and (max-width:79.9375em)": {
            display: 'block'
        },
        "@media all and (min-width:80em)": {
            marginTop: "1rem"
        }
    },
    linkWrapper: {
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifySelf: 'center',
        marginTop: 0,
        maxWidth: "35rem",
        "@media all and (max-width:79.9375em)": {
            fontSize: "var(--fz-0)",
            lineHeight: "1.75rem"
        },
        "@media all and (min-width:80em)": {
            "--links-gap": "2rem",
            columnGap: 'var(--links-gap)',
            flexDirection: 'row',
            marginTop: "1rem"
        }
    },
    link: {
        fontWeight: 400,
        fontFamily: '"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        fontSize: "var(--fz-0)",
        lineHeight: "2rem",
        "@media all and (max-width:79.9375em)": {
            marginTop: "0.25rem"
        },
        "@media all and (min-width:80em)": {
            fontWeight: 400,
            position: 'relative'
        }
    },
    linkWithSeparator: {
        "@media all and (min-width:80em)": {
            "::before": {
                content: '"|"',
                cursor: 'text',
                insetInlineStart: 'calc(var(--links-gap) / 2 * -1)',
                pointerEvents: 'none',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%) translateX(50%)',
                fontSize: "var(--fz--2)",
                lineHeight: "var(--lh--2)"
            }
        }
    },
    hideOnDesktop: {
        "@media all and (min-width:37.5em)": {
            display: 'none'
        }
    },
    hide: {
        display: 'none'
    }
});
const homepageUrl = config.get('homepageUrl');
const usePpBiData = ()=>{
    const router = useRouter();
    const currentStepName = getCurrentStepName(router.pathname);
    const excludedVariables = React.useMemo(()=>[
            'selectedOfferId'
        ], []);
    const variables = usePpDataVariables(excludedVariables);
    const { data } = usePurchasePagePersonalQuery(variables, {
        enabled: false
    });
    const pageData = data?.PurchasePage?.pageData;
    const getBiData = ()=>{
        const currentStep = pageData && isGqlPpStep(currentStepName) && getCurrentStep(currentStepName, pageData.allSteps).currentStepIndex + 1;
        const additionalInfoBiData = {
            additional_info: {
                ...currentStep ? {
                    stage_number: currentStep.toString()
                } : {}
            }
        };
        const biData = {
            stage: currentStepName,
            feature: 'Promotions footer',
            feature_type: 'Marketing',
            ...additionalInfoBiData
        };
        return biData;
    };
    return getBiData;
};
const useLgnBiData = ()=>{
    const { loweredCaseSpacedStage } = useLoginApp();
    const getBiData = ()=>{
        const biData = {
            feature: 'login footer',
            feature_type: 'Marketing',
            stage: loweredCaseSpacedStage
        };
        return biData;
    };
    return getBiData;
};
export default function LiteFooter() {
    const appType = config.get('appType');
    const promotionUrl = config.get('promotionUrl');
    const isPromotionsPage = appType === 'promotions-page';
    const biAction = useBi();
    // eslint-disable-next-line react-compiler/react-compiler
    const useBiData = isPromotionsPage ? usePpBiData : useLgnBiData;
    // eslint-disable-next-line react-compiler/react-compiler
    const getBiData = useBiData();
    const homepageHref = `${isPromotionsPage ? homepageUrl : `${homepageUrl}?htm_source=site&htm_medium=link&htm_campaign=login-footer&htm_content=homepage`}`;
    const logoOnClick = ()=>{
        biAction({
            action_id: 80,
            ...getBiData(),
            ...isPromotionsPage ? {} : {
                feature_type: 'Content',
                campaign_name: 'homepage'
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.base),
        children: [
            /*#__PURE__*/ _jsxs(HtzLink, {
                href: homepageHref,
                onClick: logoOnClick,
                "data-testid": "lite-footer-icon-link",
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        icon: 'alef',
                        styleExtend: [
                            c.icon
                        ]
                    }),
                    /*#__PURE__*/ _jsx(VisuallyHidden, {
                        children: 'To Home Page'
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(HtzLink, {
                href: homepageHref,
                onClick: logoOnClick,
                className: s9(c.homePageLink),
                "data-testid": "lite-footer-home-page-link",
                children: 'HOME PAGE'
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: s9(c.linkWrapper),
                children: [
                    isPromotionsPage ? null : /*#__PURE__*/ _jsx(HtzLink, {
                        className: s9(c.link, c.hide),
                        onClick: ()=>biAction({
                                action_id: 80,
                                ...getBiData(),
                                feature_type: 'Content',
                                campaign_name: 'homepage'
                            }),
                        href: homepageUrl,
                        children: 'HOME PAGE'
                    }),
                    /*#__PURE__*/ _jsx(HtzLink, {
                        className: s9(c.link),
                        onClick: ()=>biAction({
                                action_id: isPromotionsPage ? 50 : 3,
                                ...getBiData(),
                                ...isPromotionsPage ? {} : {
                                    campaign_name: 'subscription'
                                }
                            }),
                        href: `${promotionUrl}${isPromotionsPage ? '/groupSubscription' : '/offers?htm_source=site&htm_medium=link&htm_campaign=login-footer&htm_campaign_type=subscription'}`,
                        "data-testid": "lite-footer-organization-subscription-link",
                        children: 'GROUP PLANS'
                    }),
                    /*#__PURE__*/ _jsx(HtzLink, {
                        className: s9(c.link, c.linkWithSeparator),
                        onClick: ()=>{
                            biAction({
                                action_id: 43,
                                ...getBiData(),
                                ...isPromotionsPage ? {} : {
                                    campaign_name: 'faq'
                                }
                            });
                        },
                        href: `${homepageUrl}/misc/faq${isPromotionsPage ? '' : '?htm_source=site&htm_medium=link&htm_campaign=login-footer&htm_content=faq'}`,
                        "data-testid": "lite-footer-faq-link",
                        children: 'FAQ'
                    }),
                    /*#__PURE__*/ _jsx(HtzLink, {
                        className: s9(c.link, c.linkWithSeparator),
                        onClick: ()=>{
                            biAction({
                                action_id: 41,
                                ...getBiData(),
                                ...isPromotionsPage ? {} : {
                                    campaign_name: 'Contact Us'
                                }
                            });
                        },
                        href: `${homepageUrl}/misc/contact-us${isPromotionsPage ? '' : '?htm_source=site&htm_medium=link&htm_campaign=login-footer&htm_content=contact-us'}`,
                        "data-testid": "lite-footer-contact-us-link",
                        children: 'CONTACT US'
                    })
                ]
            })
        ]
    });
}
const colorCssVariablesHtz = [
    "footerBg",
    "footerColor"
];
const lineHeightCssVariablesHtz = [
    "--lh-1",
    "--lh-0",
    "--lh--2"
];
const fontSizeCssVariablesHtz = [
    "--fz-1",
    "--fz-0",
    "--fz--2"
];

//# sourceMappingURL=LiteFooter.js.map